import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language } from '@/lib/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const stored = localStorage.getItem('zeyn-tour-language');
    if (stored) return stored as Language;
    
    // Detect browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('tr')) return 'tr';
    if (browserLang.startsWith('ar')) return 'ar';
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('zeyn-tour-language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    
    // Apply Arabic font and direction when language is Arabic
    if (language === 'ar') {
      document.body.classList.add('font-arabic');
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.body.classList.remove('font-arabic');
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}